import { Component, h, Prop, State, Listen } from "@stencil/core";
import "../../utils/i18n";
import "../../utils/global";
import { handleClick, updateLiveLocationHelper } from "../../utils/utils";

@Component({
    tag: "shared-nav-desktop-view",
    shadow: false
})

/**
 * shared-nav-desktop-view is the desktop version of the Navigation.
 * @param {Object} navBarData - the data that will be rendered in the nav
 * @param {number} columnModifier - the number of columns that is being rendered
 * @param {number} rowModifier - the number of rows being rendered
 * @param {Array<boolean>} toggledCategoriesStateProp - the current hoverable departments
 * @param {Function} clickHandler - If passed in the Nav will use it insteaf of the default href Functionality
 */
export class SharedNavDesktopView {
    @Prop({ mutable: true }) navBarData: {};
    @Prop() toggledCategoriesStateProp: Array<boolean> = [];
    @Prop() useClickHandler?: boolean;
    @Prop() clickHandler: Function;

    @State() navBarDataUpdated: {};
    @State() currentDepartment: string;
    @State() toggledCategoriesState: Array<boolean> = [];
    @State() categoriesOpen: boolean = false;
    @State() overlayState: boolean = false;
    @State() hoverStyling: Array<boolean> = [];
    @State() useCoveoStyling = window.location.hostname === "localhost" ? "true" : "ff_usfsn_coveo_nav_styling_value";

    /**
     * Runs before the first rendering of the component.
     * This sets everything up
     */
    async componentWillLoad() {
        this.navBarDataUpdated = this.navBarData;
        this.toggledCategoriesState = this.toggledCategoriesStateProp;
        for (let i = 0; i < this.toggledCategoriesState.length; i++) {
            this.hoverStyling.push(false);
        }

        window.addEventListener("keydown", () => {
            this.updateLiveLocation();
        });
    }

    /**
     * Runs after the first load, when the real data is finished being loaded from the root
     * This sets everything up properly
     */
    async componentWillUpdate() {
        this.updateLiveLocation();
    }

    /**
     * Sets the State variables that tell the nav what the current site is.
     * This is used to show a live location to the user
     */
    @Listen("click", { target: "window" })
    async updateLiveLocation() {
        const liveLocationData = await updateLiveLocationHelper(this.navBarData);
        this.currentDepartment = liveLocationData.currentDepartment;
        this.navBarDataUpdated = liveLocationData.navBarData;
    }

    /**
     * Closes the shared nav if the user clicks out of the shared nav
     * @param event The click event
     */
    @Listen("click", { target: "window" })
    async checkCloseRequirements(event) {
        const element = document.querySelector("#shared-nav-root").shadowRoot.querySelector("#gb-tab-nav-custom");
        if (!element.contains(event.target)) {
            this.toggleCategoriesSection(0, true);
        }
    }

    /**
     * Run through the hovering array and set the index to currently being hovered
     * @param index The index of the Department that is currently being hovered
     */
    private toggleCategoriesSection = (index: number, closeAll: boolean = false) => {
        let localHovering = [];
        for (let i = 0; i < this.toggledCategoriesState.length; i++) {
            localHovering.push(false);
        }
        if (this.toggledCategoriesState[index] || closeAll) {
            this.toggledCategoriesState = localHovering;
            this.categoriesOpen = false;
        } else {
            localHovering[index] = true;
            this.toggledCategoriesState = localHovering;
            this.categoriesOpen = true;
        }
    };

    /**
     * Run through the hovering array and set all of the departments as not being hovered
     */
    private hideCategoriesHover = () => {
        // Yes I know the following is convoluted, this is apparently intended
        // https://github.com/ionic-team/stencil/issues/156
        let localHovering = [];
        for (let i = 0; i < this.hoverStyling.length; i++) {
            localHovering.push(false);
        }
        this.hoverStyling = localHovering;
    };

    private updateHoverStyling = (index) => {
        let localHovering = [];
        for (let i = 0; i < this.hoverStyling.length; i++) {
            localHovering.push(false);
        }
        localHovering[index] = true;
        this.hoverStyling = localHovering;
    };

    render() {
        let containerMaxWidthClass = "usfsn-desktop-departments-container-2000";
        let backgroundMaxWidthClass = "usfsn-desktop-departments-background-2000";
        let multicolumnMaxWidthClass = "usfsn-desktop-multicolumn-2000";
        let multicolumnContainerMaxWidthClass = "usfsn-desktop-multicolumn-container-2000";
        if (this.useCoveoStyling === "true") {
            containerMaxWidthClass = "usfsn-desktop-departments-container-1216";
            backgroundMaxWidthClass = "usfsn-desktop-departments-background-1216";
            multicolumnMaxWidthClass = "usfsn-desktop-multicolumn-1216";
            multicolumnContainerMaxWidthClass = "usfsn-desktop-multicolumn-container-1216";
        }

        return (
            <div>
                {/* The script tags are for getting Analytics Tagging outside of the Shadow Root */}
                <script src="https://brands.gm-cdn.com/_gbpe/code/prod1/analytics-event-retargeting.js"></script>

                <div>
                    <div class="usfsn-custom-nav-background">
                        {this.navBarDataUpdated && (
                            <div id={"gb-tab-nav-custom"} class={"gb-tab-nav-custom"}>
                                {/* Departments Section */}
                                <div
                                    id={"usfsn-desktop-departments-container-background"}
                                    class={backgroundMaxWidthClass + " usfsn-custom-background-color-departments"}
                                >
                                    <ul
                                        id={"usfsn-desktop-departments-container"}
                                        class={
                                            containerMaxWidthClass +
                                            " content-button-group usfsn-custom-background-color-departments usfsn-departments-bottom-border"
                                        }
                                    >
                                        {Object.keys(this.navBarDataUpdated).map((department, index) => {
                                            const contentIndex = index + 1;
                                            if (this.toggledCategoriesState[index]) {
                                                return (
                                                    <li
                                                        id={"usfsn-hovered-department-button-container"}
                                                        class={
                                                            this.hoverStyling[index]
                                                                ? "content-button active usfsn-hovered-active"
                                                                : "content-button active usfsn-hovered-inactive"
                                                        }
                                                        onClick={() => {
                                                            this.toggleCategoriesSection(index);
                                                        }}
                                                        onMouseEnter={() => {
                                                            this.updateHoverStyling(index);
                                                        }}
                                                        onMouseLeave={this.hideCategoriesHover}
                                                    >
                                                        <button
                                                            id={"usfsn-hovered-department-button-" + department}
                                                            data-tab-id={"content" + contentIndex}
                                                            class={
                                                                "usfsn-typography usfsn-department-label usfsn-hovered-department-button usfsn-department-casing usfsn-thick-border usfsn-departments-font-size usfsn-hovered-department stat-text-link"
                                                            }
                                                            data-dtm={"nav secondary"}
                                                        >
                                                            {this.navBarDataUpdated[department].name}
                                                        </button>
                                                    </li>
                                                );
                                            } else {
                                                // If the current department is where the user's webpage is currently it will enter this if statement
                                                if (department === this.currentDepartment) {
                                                    return (
                                                        <li
                                                            id={"usfsn-department-button-container"}
                                                            class={
                                                                this.hoverStyling[index]
                                                                    ? "content-button active"
                                                                    : "content-button"
                                                            }
                                                            onClick={() => {
                                                                this.toggleCategoriesSection(index);
                                                            }}
                                                            onMouseEnter={() => {
                                                                this.updateHoverStyling(index);
                                                            }}
                                                            onMouseLeave={this.hideCategoriesHover}
                                                        >
                                                            <button
                                                                id={"usfsn-department-button-" + department}
                                                                data-tab-id={"content" + contentIndex}
                                                                class={
                                                                    "usfsn-typography usfsn-department-label usfsn-department-button usfsn-department-casing usfsn-departments-font-size usfsn-active-department usfsn-thick-border stat-text-link"
                                                                }
                                                                data-dtm={"nav secondary"}
                                                            >
                                                                {this.navBarDataUpdated[department].name}
                                                            </button>
                                                        </li>
                                                    );
                                                } else {
                                                    return (
                                                        <li
                                                            id={"usfsn-department-button-container"}
                                                            class={
                                                                this.hoverStyling[index]
                                                                    ? "content-button active"
                                                                    : "content-button"
                                                            }
                                                            onClick={() => {
                                                                this.toggleCategoriesSection(index);
                                                            }}
                                                            onMouseEnter={() => {
                                                                this.updateHoverStyling(index);
                                                            }}
                                                            onMouseLeave={this.hideCategoriesHover}
                                                        >
                                                            <button
                                                                id={"usfsn-department-button-" + department}
                                                                data-tab-id={"content" + contentIndex}
                                                                class={
                                                                    "usfsn-typography usfsn-department-label usfsn-department-button usfsn-department-casing usfsn-departments-font-size stat-text-link"
                                                                }
                                                                data-dtm={"nav secondary"}
                                                            >
                                                                {this.navBarDataUpdated[department].name}
                                                            </button>
                                                        </li>
                                                    );
                                                }
                                            }
                                        })}
                                    </ul>
                                </div>
                                <hr id={"usfsn-horizontal-bar"}></hr>
                                {Object.values(this.navBarDataUpdated).map((contentArray, index) => {
                                    if (this.toggledCategoriesState[index]) {
                                        return (
                                            <div class="usfsn-custom-nav-background">
                                                <div
                                                    class={
                                                        multicolumnMaxWidthClass + " usfsn-department-home-container"
                                                    }
                                                >
                                                    <a
                                                        id="usfsn-department-home"
                                                        class={
                                                            (Object.values(this.navBarDataUpdated)[index]["home"][3]
                                                                ? " usfsn-active-category"
                                                                : "") +
                                                            " usfsn-typography usfsn-department-home stat-text-link"
                                                        }
                                                        href={Object.values(this.navBarDataUpdated)[index]["home"][1]}
                                                        data-dtm={
                                                            "nav secondary:" +
                                                            Object.values(
                                                                Object.values(this.navBarDataUpdated)[index]
                                                            )[0].toLowerCase()
                                                        }
                                                        target={
                                                            Object.values(this.navBarDataUpdated)[index]["home"][4]
                                                                ? "_blank"
                                                                : "_self"
                                                        }
                                                        rel={
                                                            Object.values(this.navBarDataUpdated)[index]["home"][4]
                                                                ? "noopener noreferrer"
                                                                : ""
                                                        }
                                                    >
                                                        {Object.values(this.navBarDataUpdated)[index]["home"][0]}
                                                    </a>
                                                </div>
                                                <div
                                                    id={"usfsn-desktop-multicolumn-container"}
                                                    class={
                                                        multicolumnContainerMaxWidthClass +
                                                        " usfsn-custom-background-color-categories"
                                                    }
                                                >
                                                    <div
                                                        class={
                                                            multicolumnMaxWidthClass +
                                                            " usfsn-desktop-multicolumn usfsn-custom-background-color-categories"
                                                        }
                                                    >
                                                        {Object.values(contentArray["columns"]).map((column) => {
                                                            return (
                                                                <div
                                                                    id={"usfsn-category-container"}
                                                                    data-content-id={"content" + (index + 1)}
                                                                    class={
                                                                        "content-container usfsn-nav-grid-container usfsn-custom-background-color-categories usfsn-category-container-top-bottom-padding"
                                                                    }
                                                                >
                                                                    {Array.isArray(column) &&
                                                                        column.map((columnArray) => {
                                                                            const dataDTMValue = Object.values(
                                                                                Object.values(this.navBarDataUpdated)[
                                                                                    index
                                                                                ]
                                                                            )[0].toLowerCase();
                                                                            if (!Array.isArray(columnArray)) {
                                                                                if (columnArray) {
                                                                                    return (
                                                                                        <p
                                                                                            class={
                                                                                                "usfsn-typography usfsn-category-header"
                                                                                            }
                                                                                        >
                                                                                            {columnArray}
                                                                                        </p>
                                                                                    );
                                                                                } else if (
                                                                                    Object.values(
                                                                                        this.navBarDataUpdated
                                                                                    )[index]["categoryHeader"]
                                                                                ) {
                                                                                    return (
                                                                                        <p
                                                                                            class={
                                                                                                "usfsn-typography usfsn-category-header usfsn-category-empty-gap"
                                                                                            }
                                                                                        >
                                                                                            {columnArray}
                                                                                        </p>
                                                                                    );
                                                                                }
                                                                            } else if (columnArray[3]) {
                                                                                return (
                                                                                    <div
                                                                                        id={
                                                                                            "usfsn-category-button-container"
                                                                                        }
                                                                                        class={
                                                                                            "usfsn-nav-category-margins"
                                                                                        }
                                                                                    >
                                                                                        <a
                                                                                            id="usfsn-category-button"
                                                                                            onClick={(event) => {
                                                                                                handleClick(
                                                                                                    event,
                                                                                                    this.clickHandler,
                                                                                                    columnArray[1],
                                                                                                    columnArray[4]
                                                                                                );
                                                                                                this.toggleCategoriesSection(
                                                                                                    0,
                                                                                                    true
                                                                                                );
                                                                                            }}
                                                                                            href={columnArray[1]}
                                                                                            class="usfsn-clickable usfsn-typography usfsn-category-label usfsn-categories-font-size usfsn-nav-no-underline usfsn-active-category stat-text-link"
                                                                                            data-dtm={
                                                                                                "nav secondary:" +
                                                                                                dataDTMValue
                                                                                            }
                                                                                            target={
                                                                                                columnArray[4]
                                                                                                    ? "_blank"
                                                                                                    : "_self"
                                                                                            }
                                                                                            rel={
                                                                                                columnArray[4]
                                                                                                    ? "noopener noreferrer"
                                                                                                    : ""
                                                                                            }
                                                                                        >
                                                                                            {columnArray[0]}
                                                                                        </a>
                                                                                    </div>
                                                                                );
                                                                            } else {
                                                                                return (
                                                                                    <div
                                                                                        id={
                                                                                            "usfsn-category-button-container"
                                                                                        }
                                                                                        class={
                                                                                            "usfsn-nav-category-margins"
                                                                                        }
                                                                                    >
                                                                                        <a
                                                                                            id="usfsn-category-button"
                                                                                            onClick={(event) => {
                                                                                                handleClick(
                                                                                                    event,
                                                                                                    this.clickHandler,
                                                                                                    columnArray[1],
                                                                                                    columnArray[4]
                                                                                                );
                                                                                                this.toggleCategoriesSection(
                                                                                                    0,
                                                                                                    true
                                                                                                );
                                                                                            }}
                                                                                            href={columnArray[1]}
                                                                                            class="usfsn-clickable usfsn-typography usfsn-category-label usfsn-categories-font-size usfsn-nav-no-underline stat-text-link"
                                                                                            data-dtm={
                                                                                                "nav secondary:" +
                                                                                                dataDTMValue
                                                                                            }
                                                                                            target={
                                                                                                columnArray[4]
                                                                                                    ? "_blank"
                                                                                                    : "_self"
                                                                                            }
                                                                                            rel={
                                                                                                columnArray[4]
                                                                                                    ? "noopener noreferrer"
                                                                                                    : ""
                                                                                            }
                                                                                        >
                                                                                            {columnArray[0]}
                                                                                        </a>
                                                                                    </div>
                                                                                );
                                                                            }
                                                                        })}
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    }
                                })}
                            </div>
                        )}
                    </div>
                    {/* This is for the backdrop that shows up when the nav is expanded */}
                    {this.categoriesOpen && <div id="usfsn-nav-backdrop" class={"usfsn-modal-backdrop"} />}
                </div>
            </div>
        );
    }
}
